import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Select, { components } from "react-select";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBan,
  faCheckCircle,
  faClipboardList,
  faCoins,
  faCopy,
  faEye,
  faFileAlt,
  faFileUpload,
  faSave,
  faShieldAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import MemberViewModal from "../Member/MemberViewModal";
import RiBenefitsTab from "./RiBenefitsTab";
import RiDocumentsTab from "./RiDocumentsTab";

const MySwal = withReactContent(Swal);
const MAX_BANK_ACCOUNT_DETAILS_LENGTH = 200;

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (data?.message && typeof data.message === "object") {
    const firstField = Object.values(data.message)[0];
    if (Array.isArray(firstField) && firstField[0]) {
      return firstField[0];
    }
  }

  return fallbackMessage;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("authentication/token") ||
    localStorage.getItem("auth_token") ||
    "";

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const getLoggedUser = () => {
  return (
    localStorage.getItem("name") ||
    localStorage.getItem("username") ||
    localStorage.getItem("user_name") ||
    "Web Portal"
  );
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");

const STATUS_CONFIG = {
  "10": { label: "Saved / Received", color: "bg-amber-100 text-amber-800 border border-amber-200" },
  "11": { label: "Endorsed for Processing", color: "bg-blue-100 text-blue-800 border border-blue-200" },
  "06": { label: "Processed", color: "bg-green-100 text-green-800 border border-green-200" },
  "07": { label: "Invalid Documents", color: "bg-red-100 text-red-800 border border-red-200" },
  "04": { label: "Cancelled", color: "bg-gray-100 text-gray-800 border border-gray-200" },
};

const getCleanHospitalName = (provider) => {
  if (!provider) return "";

  const rawName =
    provider.provider_name ||
    provider.label ||
    provider.text ||
    "";

  return String(rawName).replace(/^\s*\[[^\]]+\]\s*/, "").trim();
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "42px",
    borderColor: state.isFocused ? "#f97316" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(249, 115, 22, 0.12)" : "none",
    "&:hover": {
      borderColor: "#f97316",
    },
    fontSize: "14px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#ea580c"
      : state.isFocused
      ? "#fff7ed"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#374151",
    fontSize: "14px",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
};

const LoadingIndicator = (props) => (
  <components.LoadingIndicator {...props}>
    <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-orange-500" />
  </components.LoadingIndicator>
);

const TABS = [
  { id: "form", label: "RI Form", icon: faFileAlt },
  { id: "benefits", label: "Benefits & ICD", icon: faCoins },
  { id: "documents", label: "Documents", icon: faFileUpload },
  { id: "actions", label: "Actions", icon: faClipboardList },
];

const DEFAULT_FORM = {
  member_id: "",
  provider_code: "",
  member_name: "",
  plan_code: "",
  coverage_code: "",
  generated_by: "Web Portal",
  date_filed: "",
  type_of_claim: "",
  patient_name: "",
  patient_member_id: "",
  principal_name: "",
  principal_member_id: "",
  company_name: "",
  corp_code: "",
  office_tel_no: "",
  email_address: "",
  mobile_no: "",
  hospital_name: "",
  icd_code: "",
  date_of_medical_treatment: "",
  total_amt_of_claim: "",
  ri_method: "",
  pickup_by: "",
  bank_account_details: "",
  remarks: "",
  bank_name: "",
  bank_branch: "",
  acct_no: "",
  bank_acct_name: "",
};

const ReimbursementAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    ...DEFAULT_FORM,
    generated_by: getLoggedUser(),
  });
  const [providers, setProviders] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [coverages, setCoverages] = useState([]);
  const [typeOfClaimCoverages, setTypeOfClaimCoverages] = useState([]);
  const [banks, setBanks] = useState([]);
  const [icdOptions, setIcdOptions] = useState([]);
  const [memberCheck, setMemberCheck] = useState(null);
  const [ruleResult, setRuleResult] = useState(null);
  const [existingRiForm, setExistingRiForm] = useState(null);
  const [mode, setMode] = useState("create");
  const [loading, setLoading] = useState(false);
  const [checkingMember, setCheckingMember] = useState(false);
  const [checkingRule, setCheckingRule] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [activeTab, setActiveTab] = useState("form");
  const [actionLoading, setActionLoading] = useState(false);
  const [typeOfClaimSearch, setTypeOfClaimSearch] = useState("");
  const [searchInput, setSearchInput] = useState({ member: "", provider: "" });
  const [selectorLoading, setSelectorLoading] = useState({ member: false, provider: false });
  const [icdLoading, setIcdLoading] = useState(false);
  const [viewMemberModalId, setViewMemberModalId] = useState("");
  const [benefitsDirty, setBenefitsDirty] = useState(false);

  const transNo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("trans_no") || "";
  }, [location.search]);

  const selectedTypeOfClaims = useMemo(() => {
    if (!form.type_of_claim) return [];
    return form.type_of_claim
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [form.type_of_claim]);

  const filteredTypeOfClaimCoverages = useMemo(() => {
    const keyword = typeOfClaimSearch.trim().toLowerCase();
    if (!keyword) return typeOfClaimCoverages;

    return typeOfClaimCoverages.filter((coverage) => {
      const code = (coverage?.coverage_code || "").toLowerCase();
      const desc = (coverage?.coverage_desc || "").toLowerCase();
      return code.includes(keyword) || desc.includes(keyword);
    });
  }, [typeOfClaimCoverages, typeOfClaimSearch]);

  const selectedBankOption = useMemo(() => {
    if (!form.bank_name) return null;

    const matchedBank = banks.find(
      (bank) => (bank?.bank_name || "").trim() === form.bank_name.trim()
    );

    if (matchedBank) {
      return matchedBank;
    }

    return {
      tbl_id: form.bank_name,
      bank_name: form.bank_name,
    };
  }, [banks, form.bank_name]);

  const selectedIcdOption = useMemo(() => {
    if (!form.icd_code) return null;

    const matchedIcd = icdOptions.find(
      (icd) => String(icd?.tbl_id || "") === String(form.icd_code)
    );

    if (matchedIcd) {
      return matchedIcd;
    }

    return {
      tbl_id: form.icd_code,
      condition: `ICD ID ${form.icd_code}`,
    };
  }, [icdOptions, form.icd_code]);

  const onChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCopyValue = useCallback(async (label, value) => {
    const text = String(value ?? "").trim();
    if (!text) {
      toast.error(`No ${label.toLowerCase()} to copy.`);
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toast.success(`${label} copied.`);
    } catch (error) {
      toast.error(`Failed to copy ${label.toLowerCase()}.`);
    }
  }, []);

  const getOptionLabel = (option) => {
    if (!option) return "";
    return option.text || option.label || option.provider_name || option.member_name || option.bank_name || option.id || "";
  };

  const getOptionValue = (option) => {
    if (!option) return "";
    return option.id || option.member_id || option.provider_code || option.tbl_id || option.value || "";
  };

  const getIcdOptionLabel = (option) => {
    if (!option) return "";
    const code = option.icd_10_cm || option.icd_code || option.icd10_code || option.code || "";
    const categDesc = option.categ_desc || "";
    const condition = option.condition || option.description || option.label || "";
    const details = [categDesc, condition ? `(${condition})` : ""].filter(Boolean).join(" ");
    return [code, details].filter(Boolean).join(" ").trim();
  };

  const loadIcdOptions = useCallback(async (query = "") => {
    setIcdLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/reimbursement/reference/icd`, {
        params: { q: query || "", limit: 20 },
        headers: getAuthHeaders(),
      });

      if (response?.data?.status === "success") {
        setIcdOptions(response?.data?.data?.icd_codes || []);
        return;
      }

      setIcdOptions([]);
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      setIcdOptions([]);
    } finally {
      setIcdLoading(false);
    }
  }, []);

  const loadMemberDetails = useCallback(async (memberId) => {
    if (!memberId?.trim()) {
      return;
    }

    setCheckingMember(true);
    setMemberCheck(null);
    try {
      const response = await axios.post(
        `${BASE_URL}/reimbursement/check-member`,
        {
          member_id: memberId.trim(),
        },
        {
          headers: getAuthHeaders(),
        }
      );

      const member = response?.data?.data?.member;
      setMemberCheck(member || null);
      if (member) {
        const matchedBank = banks.find(
          (bank) => String(bank?.tbl_id || "") === String(member.bank_id || "")
        );

        setForm((prev) => ({
          ...prev,
          member_id: member.member_id || memberId,
          member_name: `${member.first_name || ""} ${member.last_name || ""}`.trim(),
          plan_code: member.plan_code || "",
          corp_code: member.corp_code || "",
          principal_name: member.principal_name || "",
          principal_member_id: member.principal_id || "",
          company_name: member.corp_name || prev.company_name,
          office_tel_no: member.corp_tel_no || prev.office_tel_no,
          email_address: member.email_add || member.corp_email_add || prev.email_address,
          patient_name:
            prev.patient_name ||
            `${member.first_name || ""} ${member.last_name || ""}`.trim(),
          patient_member_id: prev.patient_member_id || member.member_id || "",
          mobile_no: member.mob_tel || prev.mobile_no,
          bank_name: matchedBank?.bank_name || "",
          bank_branch: member.bank_branch || "",
          acct_no: member.bank_account_no || "",
          bank_acct_name: member.bank_acct_name || "",
        }));
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      setMemberCheck(null);
      toast.error(error?.response?.data?.message || "Failed to load member details.");
    } finally {
      setCheckingMember(false);
    }
  }, [banks]);

  const fetchReferences = async () => {
    try {
      const [memberRes, providerRes, coverageRes, bankRes] = await Promise.all([
        axios.get(`${BASE_URL}/reimbursement/members/search`, {
          params: { q: "" },
          headers: getAuthHeaders(),
        }),
        axios.get(`${BASE_URL}/generate-loa/providers/search`, {
          params: { q: "" },
          headers: getAuthHeaders(),
        }),
        axios.get(`${BASE_URL}/reimbursement/reference/coverages`, {
          headers: getAuthHeaders(),
        }),
        axios.get(`${BASE_URL}/reimbursement/reference/banks`, {
          headers: getAuthHeaders(),
        }),
      ]);

      setMemberOptions(memberRes?.data?.data || []);
      setProviders(providerRes?.data?.data || []);
      setProviderOptions(providerRes?.data?.data || []);
      const coverageList = coverageRes?.data?.data?.coverages || [];
      setCoverages(coverageList);
      setTypeOfClaimCoverages(coverageRes?.data?.data?.type_of_claim_coverages || []);
      setBanks(bankRes?.data?.data?.banks || []);
      loadIcdOptions("");

      setForm((prev) => {
        if (prev.coverage_code) {
          return prev;
        }

        const inpatientCoverage = coverageList.find(
          (coverage) => String(coverage?.coverage_code || "").toUpperCase() === "RI-INPATIENT"
        );

        return {
          ...prev,
          coverage_code: inpatientCoverage?.coverage_code || prev.coverage_code,
        };
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      setMemberOptions([]);
      setProviders([]);
      setProviderOptions([]);
      setCoverages([]);
      setTypeOfClaimCoverages([]);
      setBanks([]);
      setIcdOptions([]);
      toast.error(getApiErrorMessage(error, "Failed to load reference data."));
    }
  };

  const debouncedIcdSearch = useMemo(() => {
    let timeoutId;
    return (query) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        loadIcdOptions(query);
      }, 300);
    };
  }, [loadIcdOptions]);

  const handleIcdInputChange = useCallback(
    (inputValue, meta) => {
      if (meta.action !== "input-change") {
        return inputValue;
      }
      debouncedIcdSearch(inputValue);
      return inputValue;
    },
    [debouncedIcdSearch]
  );

  const debouncedSearch = useMemo(() => {
    const timeoutIds = {};

    return (type, query, setter) => {
      clearTimeout(timeoutIds[type]);
      timeoutIds[type] = setTimeout(async () => {
        setSelectorLoading((prev) => ({ ...prev, [type]: true }));
        try {
          const searchEndpoint =
            type === "member"
              ? `${BASE_URL}/reimbursement/members/search`
              : `${BASE_URL}/generate-loa/${type}s/search`;

          const response = await axios.get(searchEndpoint, {
            params: { q: query || "" },
            headers: getAuthHeaders(),
          });

          if (response?.data?.status === "success") {
            setter(response?.data?.data || []);
            return;
          }

          setter([]);
        } catch (error) {
          if (error?.response?.status === 401) {
            window.location.href = "/login";
          }
          setter([]);
        } finally {
          setSelectorLoading((prev) => ({ ...prev, [type]: false }));
        }
      }, 300);
    };
  }, []);

  const handleSelectorInputChange = useCallback(
    (type, setter) => (inputValue, meta) => {
      if (meta.action !== "input-change") {
        return inputValue;
      }

      setSearchInput((prev) => ({ ...prev, [type]: inputValue }));
      debouncedSearch(type, inputValue, setter);
      return inputValue;
    },
    [debouncedSearch]
  );

  const handleMemberSelect = useCallback(
    async (selected) => {
      setSelectedMember(selected);
      setForm((prev) => ({
        ...prev,
        member_id: selected?.id || "",
      }));

      if (selected?.id) {
        await loadMemberDetails(selected.id);
      } else {
        setMemberCheck(null);
        setForm((prev) => ({
          ...prev,
          member_id: "",
          member_name: "",
          plan_code: "",
          corp_code: "",
          principal_name: "",
          principal_member_id: "",
          company_name: "",
          office_tel_no: "",
          email_address: "",
          patient_name: "",
          patient_member_id: "",
          mobile_no: "",
        }));
      }
    },
    [loadMemberDetails]
  );

  const handleProviderSelect = useCallback((selected) => {
    setSelectedProvider(selected);
    setForm((prev) => ({
      ...prev,
      provider_code: selected?.id || "",
      hospital_name: getCleanHospitalName(selected),
    }));
  }, []);

  const toggleTypeOfClaimSelection = (coverageCode) => {
    const current = [...selectedTypeOfClaims];
    const exists = current.includes(coverageCode);
    const next = exists
      ? current.filter((code) => code !== coverageCode)
      : [...current, coverageCode];
    onChange("type_of_claim", next.join(","));
  };

  const loadTransactionIfEditing = async () => {
    if (!transNo) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/reimbursement/transactions/${encodeURIComponent(transNo)}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const ri = response?.data?.data?.ri_form;
      if (ri) {
        setMode("view");
        setExistingRiForm(ri);
        setForm((prev) => ({
          ...prev,
          ...ri,
          total_amt_of_claim: ri.total_amt_of_claim || "",
        }));
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
  }, []);

  useEffect(() => {
    if (transNo) {
      loadTransactionDetails();
      loadTransactionIfEditing();
    }
  }, [transNo]);

  const handleRuleCheck = async () => {
    if (!form.member_id || !form.coverage_code) {
      return;
    }

    setCheckingRule(true);
    setRuleResult(null);
    try {
      const response = await axios.post(
        `${BASE_URL}/reimbursement/rule-check`,
        {
          member_id: form.member_id,
          provider_code: form.provider_code,
          coverage_code: form.coverage_code,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      setRuleResult(response?.data?.data || null);
      toast.success("Rule check completed.");
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      setRuleResult(null);
      toast.error(getApiErrorMessage(error, "Rule check failed."));
    } finally {
      setCheckingRule(false);
    }
  };

  // ── Details-mode functions ──
  const loadTransactionDetails = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/reimbursement/transactions/filterable`,
        {
          params: { search: transNo, page: 1, limit: 5 },
          headers: getAuthHeaders(),
        }
      );
      const rows = res?.data?.data?.transactions || [];
      const txn = rows.find((r) => r.trans_no === transNo) || rows[0] || null;
      setTransactionData(txn);
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
    }
  };

  const handleBenefitsSaved = async (payload) => {
    await Promise.all([loadTransactionDetails(), loadTransactionIfEditing()]);

    if (payload?.updatedRiFormTotal != null) {
      setForm((prev) => ({
        ...prev,
        total_amt_of_claim: String(payload.updatedRiFormTotal),
      }));
      toast.success("RI Form Total Amount of Claim updated from Benefits total.");
    }

    setBenefitsDirty(false);
  };

  const handleTabChange = async (nextTabId) => {
    if (nextTabId === activeTab) return;

    if (activeTab === "benefits" && nextTabId !== "benefits" && benefitsDirty) {
      const res = await MySwal.fire({
        icon: "warning",
        title: "Unsaved Changes in Benefits & ICD",
        html: "<p>Please save your changes first. Kapag umalis ka sa tab na ito, mawawala ang unsaved changes.</p>",
        showCancelButton: true,
        confirmButtonText: "Stay in Benefits Tab",
        cancelButtonText: "Leave Without Saving",
        confirmButtonColor: "#ea580c",
      });

      if (res.isConfirmed) {
        return;
      }

      setBenefitsDirty(false);
    }

    setActiveTab(nextTabId);
  };

  const handleEndorse = async () => {
    const result = await MySwal.fire({
      icon: "question",
      title: "Endorse Reimbursement?",
      html: `<p>This will endorse transaction <strong>${transNo}</strong> for processing.</p>`,
      showCancelButton: true,
      confirmButtonText: "Yes, Endorse",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/reimbursement/transactions/${transNo}/endorse`,
        { endorsed_by: getLoggedUser() },
        { headers: getAuthHeaders() }
      );
      toast.success("Transaction endorsed successfully.");
      loadTransactionDetails();
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(error?.response?.data?.message || "Failed to endorse.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    const result = await MySwal.fire({
      icon: "warning",
      title: "Cancel Reimbursement?",
      html: "<p>Please provide a reason for cancellation:</p>",
      input: "textarea",
      inputLabel: "Cancellation Reason",
      inputPlaceholder: "Enter reason for cancellation...",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Transaction",
      cancelButtonText: "Keep Transaction",
      confirmButtonColor: "#dc2626",
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Please enter a cancellation reason.";
      },
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/reimbursement/transactions/${transNo}/cancel`,
        { cancelled_details: result.value, cancelled_by: getLoggedUser() },
        { headers: getAuthHeaders() }
      );
      toast.success("Transaction cancelled.");
      loadTransactionDetails();
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(error?.response?.data?.message || "Failed to cancel.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcess = async () => {
    setActionLoading(true);
    let benefits = [];
    try {
      const bRes = await axios.get(
        `${BASE_URL}/reimbursement/transactions/${transNo}/benefits/saved`,
        { headers: getAuthHeaders() }
      );
      benefits = bRes?.data?.data?.benefits || [];
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }

    if (!benefits.length) {
      MySwal.fire({
        icon: "warning",
        title: "No Benefits Saved",
        text: "Please save benefit codes and amounts in the Benefits & ICD tab first.",
      });
      return;
    }

    const benefitCodes = benefits.map((b) => b.benefit_code);
    const amounts = benefits.map((b) => Number(b.amount || 0));
    const totalAmount = amounts.reduce((s, a) => s + a, 0);

    const benefitRows = benefits
      .map(
        (b, i) =>
          `<tr><td class="text-left py-1 pr-4 font-mono text-xs">${b.benefit_code}</td><td class="text-right py-1 text-sm">${formatCurrency(amounts[i])}</td></tr>`
      )
      .join("");

    const result = await MySwal.fire({
      icon: "question",
      title: "Process Reimbursement?",
      html: `<div class="text-left"><table class="w-full mb-2"><thead><tr><th class="text-left text-xs text-gray-500 pb-1">Benefit</th><th class="text-right text-xs text-gray-500 pb-1">Amount</th></tr></thead><tbody>${benefitRows}</tbody><tfoot><tr class="border-t border-gray-200"><td class="text-left pt-2 font-semibold text-sm">Total</td><td class="text-right pt-2 font-bold text-green-700">${formatCurrency(totalAmount)}</td></tr></tfoot></table></div>`,
      showCancelButton: true,
      confirmButtonText: "Yes, Process",
      cancelButtonText: "Go Back",
      confirmButtonColor: "#16a34a",
      width: "480px",
    });
    if (!result.isConfirmed) return;

    const user = getLoggedUser();
    setActionLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/reimbursement/transactions/${transNo}/process`,
        {
          amounts,
          benefit_codes: benefitCodes,
          total_amount: totalAmount,
          issued_by: user,
          update_by: user,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Transaction ${transNo} processed successfully.`);
      MySwal.fire({
        icon: "success",
        title: "Processed!",
        text: `Transaction ${transNo} has been processed. Total: ${formatCurrency(totalAmount)}`,
      });
      loadTransactionDetails();
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(error?.response?.data?.message || "Failed to process.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderTransactionActionButtons = () => {
    const currentStatus = transactionData?.trans_flag_status || "";
    const isTerminal = currentStatus === "06" || currentStatus === "04";

    if (isTerminal) {
      return (
        <p className="text-sm text-gray-500">
          This transaction is in a terminal state and cannot be modified.
        </p>
      );
    }

    return (
      <div className="flex flex-wrap gap-3">
        {currentStatus === "10" && (
          <button
            onClick={handleEndorse}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {actionLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faCheckCircle} />
            )}
            Endorse for Processing
          </button>
        )}
        {currentStatus === "11" && (
          <button
            onClick={handleProcess}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
          >
            {actionLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faCheckCircle} />
            )}
            Process Reimbursement
          </button>
        )}
        <button
          onClick={handleCancel}
          disabled={actionLoading}
          className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
        >
          {actionLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <FontAwesomeIcon icon={faBan} />
          )}
          Cancel Transaction
        </button>
      </div>
    );
  };

  const canSubmit =
    mode === "create" &&
    form.member_id &&
    form.member_name &&
    form.plan_code &&
    form.coverage_code &&
    form.date_filed &&
    form.type_of_claim &&
    form.patient_name &&
    form.patient_member_id &&
    form.corp_code;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    if ((form.bank_account_details || "").length > MAX_BANK_ACCOUNT_DETAILS_LENGTH) {
      toast.error(`Bank account details must not exceed ${MAX_BANK_ACCOUNT_DETAILS_LENGTH} characters.`);
      return;
    }

    const payload = {
      ...form,
      hospital_name: getCleanHospitalName({
        provider_name: form.hospital_name,
      }),
      total_amt_of_claim: Number(form.total_amt_of_claim || 0),
    };

    const summaryHtml = `
      <div style="text-align:left; font-size:13px; color:#1f2937;">
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:12px;">
          <div style="padding:10px; border:1px solid #fed7aa; border-radius:10px; background:#fff7ed;">
            <div style="font-size:11px; text-transform:uppercase; color:#9a3412;">Member</div>
            <div style="font-weight:700; margin-top:4px;">${escapeHtml(form.member_name || "-")}</div>
            <div style="font-size:12px; color:#6b7280;">${escapeHtml(form.member_id || "-")}</div>
          </div>
          <div style="padding:10px; border:1px solid #fed7aa; border-radius:10px; background:#fff7ed;">
            <div style="font-size:11px; text-transform:uppercase; color:#9a3412;">Provider</div>
            <div style="font-weight:700; margin-top:4px;">${escapeHtml(form.hospital_name || "-")}</div>
            <div style="font-size:12px; color:#6b7280;">${escapeHtml(form.provider_code || "-")}</div>
          </div>
        </div>
        <div style="border:1px solid #e5e7eb; border-radius:10px; padding:10px; background:#ffffff; margin-bottom:12px;">
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
            <div><strong>Coverage:</strong> ${escapeHtml(form.coverage_code || "-")}</div>
            <div><strong>Type of Claim:</strong> ${escapeHtml(form.type_of_claim || "-")}</div>
            <div><strong>Date Filed:</strong> ${escapeHtml(form.date_filed || "-")}</div>
            <div><strong>Date of Treatment:</strong> ${escapeHtml(form.date_of_medical_treatment || "-")}</div>
                <div><strong>(ICD Codes) Diagnosis:</strong> ${escapeHtml(form.icd_code || "-")}</div>
            <div><strong>Total Claim:</strong> ${escapeHtml(formatCurrency(payload.total_amt_of_claim))}</div>
            <div><strong>RI Method:</strong> ${escapeHtml(form.ri_method || "-")}</div>
          </div>
        </div>
        <details style="border:1px dashed #cbd5e1; border-radius:10px; padding:10px; background:#f8fafc;">
          <summary style="cursor:pointer; font-weight:700; color:#0f172a;">I.T Reference: Database Queries (Create Reimbursement)</summary>
          <pre style="white-space:pre-wrap; margin-top:8px; font-size:12px; color:#334155;">START TRANSACTION;
INSERT INTO transactions (member_id, corp_code, plan_code, provider_code, coverage_code, trans_date, trans_flag_status, trans_type) VALUES (...);
SELECT LAST_INSERT_ID() AS tbl_id;
UPDATE transactions SET trans_no = CONCAT(DATE_FORMAT(NOW(), '%y%m'), LPAD(tbl_id, 6, '0')) WHERE tbl_id = LAST_INSERT_ID();
INSERT INTO ri_form (trans_no, member_id, member_name, plan_code, coverage_code, date_filed, type_of_claim, patient_name, patient_member_id, principal_name, principal_member_id, company_name, corp_code, office_tel_no, email_address, mobile_no, hospital_name, date_of_medical_treatment, total_amt_of_claim, ri_method, pickup_by, bank_account_details, remarks, bank_name, bank_branch, acct_no, bank_acct_name, status, created_at, updated_at) VALUES (...);
INSERT INTO ri_history (trans_no, update_for, update_from, message, update_by, remarks, created_at, updated_at) VALUES (...);
COMMIT;</pre>
        </details>
      </div>
    `;

    const confirmCreate = await MySwal.fire({
      icon: "info",
      title: "Review Reimbursement Summary",
      html: summaryHtml,
      width: "900px",
      showCancelButton: true,
      confirmButtonText: "Proceed Create Reimbursement",
      cancelButtonText: "Back to Form",
      confirmButtonColor: "#ea580c",
    });

    if (!confirmCreate.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/reimbursement/transactions`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );

      const createdTransNo = response?.data?.data?.trans_no;
      toast.success(response?.data?.message || "Reimbursement created successfully.");
      if (createdTransNo) {
        navigate(`/transaction-reimbursement-add?trans_no=${createdTransNo}`);
        return;
      }
      navigate("/transaction-reimbursement-list");
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      toast.error(getApiErrorMessage(error, "Failed to create reimbursement."));
    } finally {
      setLoading(false);
    }
  };

  // ── Details (tabbed) mode ──
  if (transNo) {
    const statusConf =
      STATUS_CONFIG[transactionData?.trans_flag_status] || {
        label: transactionData?.trans_flag_status || "Unknown",
        color: "bg-gray-100 text-gray-800 border border-gray-200",
      };
    const currentStatus = transactionData?.trans_flag_status || "";
    const isTerminal = currentStatus === "06" || currentStatus === "04";
    const displayPatientName =
      form.patient_name ||
      transactionData?.patient_name ||
      form.member_name ||
      transactionData?.full_name ||
      "";
    const displayPatientMemberId =
      form.patient_member_id ||
      transactionData?.patient_member_id ||
      form.member_id ||
      transactionData?.member_id ||
      "";
    const displayPrincipalName =
      form.principal_name ||
      transactionData?.principal_name ||
      "";
    const displayPrincipalMemberId =
      form.principal_member_id ||
      transactionData?.principal_member_id ||
      "";
    const patientViewMemberId =
      displayPatientMemberId || form.member_id || transactionData?.member_id || "";
    const principalViewMemberId = displayPrincipalMemberId || "";
    const icdDisplayValue = form.icd_code
      ? getIcdOptionLabel(selectedIcdOption) || String(form.icd_code)
      : "";

    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4 md:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          {/* Header */}
          <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Reimbursement {transNo}
                  </h1>
                  {transactionData && (
                    <span
                      className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusConf.color}`}
                    >
                      {statusConf.label}
                    </span>
                  )}
                </div>
                {transactionData && (
                  <p className="text-sm text-gray-600">
                    {transactionData.full_name} — {transactionData.corp_name} —{" "}
                    {transactionData.coverage_code}
                  </p>
                )}
              </div>
              <button
                onClick={() => navigate("/transaction-reimbursement-list")}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to List
              </button>
              <div className="w-full md:w-auto md:max-w-md md:self-end">
                {renderTransactionActionButtons()}
              </div>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex overflow-x-auto rounded-2xl border border-orange-100 bg-white shadow-sm">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                disabled={loading}
                className={`flex flex-1 items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
                  activeTab === tab.id
                    ? "border-b-2 border-orange-500 text-orange-700"
                    : "text-gray-500 hover:text-gray-700"
                } ${loading ? "cursor-not-allowed opacity-70" : ""}`}
              >
                <FontAwesomeIcon icon={tab.icon} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-64">
            {/* FORM TAB */}
            {activeTab === "form" &&
              (loading ? (
                <div className="animate-pulse rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                  <div className="mb-4 h-6 w-40 rounded bg-gray-200" />
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(12)].map((_, idx) => (
                      <div key={`ri-form-skeleton-${idx}`} className="space-y-2">
                        <div className="h-3 w-24 rounded bg-gray-100" />
                        <div className="h-4 w-36 rounded bg-gray-200" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                  <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                    <div className="mb-5 flex flex-col gap-3 border-b border-orange-100 pb-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          RI Form Details
                        </h2>
                        <p className="text-sm text-gray-500">
                          Viewing submitted reimbursement information for transaction {transNo}.
                        </p>
                      </div>
                      <div className="inline-flex items-center rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                        {form.coverage_code || transactionData?.coverage_code || "No Coverage"}
                      </div>
                    </div>

                    <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Patient Name
                        </p>
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="min-w-0 flex-1 break-words text-sm font-semibold text-gray-800">{displayPatientName || "—"}</p>
                          <button
                            type="button"
                            onClick={() => handleCopyValue("Patient Name", displayPatientName)}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                            title="Copy patient name"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-xs" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setViewMemberModalId(patientViewMemberId)}
                          disabled={!patientViewMemberId}
                          className="mt-2 inline-flex items-center gap-2 rounded-md border border-orange-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Patient Info
                        </button>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Patient Member ID
                        </p>
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="min-w-0 flex-1 break-all text-sm font-semibold text-gray-800">{displayPatientMemberId || "—"}</p>
                          <button
                            type="button"
                            onClick={() => handleCopyValue("Patient Member ID", displayPatientMemberId)}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                            title="Copy patient member ID"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-xs" />
                          </button>
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Principal Name
                        </p>
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="min-w-0 flex-1 break-words text-sm font-semibold text-gray-800">{displayPrincipalName || "—"}</p>
                          <button
                            type="button"
                            onClick={() => handleCopyValue("Principal Name", displayPrincipalName)}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                            title="Copy principal name"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-xs" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setViewMemberModalId(principalViewMemberId)}
                          disabled={!principalViewMemberId}
                          className="mt-2 inline-flex items-center gap-2 rounded-md border border-orange-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Principal Info
                        </button>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Principal Member ID
                        </p>
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="min-w-0 flex-1 break-all text-sm font-semibold text-gray-800">{displayPrincipalMemberId || "—"}</p>
                          <button
                            type="button"
                            onClick={() => handleCopyValue("Principal Member ID", displayPrincipalMemberId)}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                            title="Copy principal member ID"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-xs" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                      <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 lg:col-span-2">
                        {[
                          { label: "Date Filed", value: form.date_filed },
                          { label: "Date of Treatment", value: form.date_of_medical_treatment },
                          { label: "Total Amount of Claim", value: formatCurrency(form.total_amt_of_claim) },
                          { label: "ICD Code", value: form.icd_code },
                          { label: "ICD Code and Details", value: icdDisplayValue },
                          { label: "RI Method", value: form.ri_method },
                          { label: "Company", value: form.company_name },
                          { label: "Office Tel No", value: form.office_tel_no },
                          { label: "Email", value: form.email_address, copyable: true },
                          { label: "Mobile", value: form.mobile_no, copyable: true },
                          { label: "Bank Acct. Details from App/Other details", value: form.bank_account_details },
                          { label: "Bank Name", value: form.bank_name },
                          { label: "Bank Branch", value: form.bank_branch },
                          { label: "Account No", value: form.acct_no },
                          { label: "Account Name", value: form.bank_acct_name },
                        ].map(({ label, value, copyable }) => (
                          <div key={label} className="flex items-start justify-between gap-4 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                            <div className="flex min-w-0 items-start gap-2">
                              <p className="min-w-0 break-words text-right text-sm font-medium text-gray-800">{value || "—"}</p>
                              {copyable ? (
                                <button
                                  type="button"
                                  onClick={() => handleCopyValue(label, value)}
                                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                                  title={`Copy ${label}`}
                                >
                                  <FontAwesomeIcon icon={faCopy} className="text-xs" />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-orange-700">
                          Type of Claim
                        </p>
                        {selectedTypeOfClaims.length ? (
                          <ul className="space-y-2">
                            {selectedTypeOfClaims.map((claimCode) => (
                              <li key={claimCode} className="rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-orange-800">
                                {claimCode}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="rounded-lg border border-dashed border-orange-200 bg-white px-3 py-2 text-sm text-gray-500">
                            No Type of Claim selected.
                          </p>
                        )}
                      </div>
                    </div>

                    {form.remarks && (
                      <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Remarks
                        </p>
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">{form.remarks}</p>
                      </div>
                    )}
                  </div>
                ))}

            {/* BENEFITS TAB */}
            {activeTab === "benefits" &&
              (loading ? (
                <div className="animate-pulse rounded-2xl border border-orange-100 bg-white p-5 shadow-sm space-y-4">
                  <div className="h-6 w-36 rounded bg-gray-200" />
                  <div className="h-10 w-1/2 rounded bg-gray-200" />
                  <div className="h-52 w-full rounded-xl bg-gray-100" />
                  <div className="ml-auto h-10 w-48 rounded bg-gray-200" />
                </div>
              ) : (
                  <RiBenefitsTab
                    transNo={transNo}
                    memberId={
                      transactionData?.member_id || form.patient_member_id
                    }
                    initialIcdCode={form.icd_code || ""}
                    riFormTotalAmount={form.total_amt_of_claim || 0}
                    onSaved={handleBenefitsSaved}
                    onDirtyChange={setBenefitsDirty}
                  />
                ))}

            {/* DOCUMENTS TAB */}
            {activeTab === "documents" &&
              (loading ? (
                <div className="animate-pulse rounded-2xl border border-orange-100 bg-white p-5 shadow-sm space-y-4">
                  <div className="h-6 w-44 rounded bg-gray-200" />
                  <div className="h-10 w-56 rounded bg-gray-200" />
                  <div className="h-56 w-full rounded-xl bg-gray-100" />
                </div>
              ) : (
                  <RiDocumentsTab
                    transNo={transNo}
                    coverageCode={
                      form.coverage_code ||
                      transactionData?.coverage_code ||
                      ""
                    }
                  />
                ))}

            {/* ACTIONS TAB */}
            {activeTab === "actions" &&
              (loading ? (
                <div className="animate-pulse rounded-2xl border border-orange-100 bg-white p-5 shadow-sm space-y-4">
                  <div className="h-6 w-40 rounded bg-gray-200" />
                  <div className="h-14 w-full rounded-xl bg-gray-100" />
                  <div className="flex flex-wrap gap-3">
                    <div className="h-10 w-44 rounded bg-gray-200" />
                    <div className="h-10 w-44 rounded bg-gray-200" />
                    <div className="h-10 w-40 rounded bg-gray-200" />
                  </div>
                </div>
              ) : (
                  <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">
                      Transaction Actions
                    </h2>
                    <div className="mb-6 rounded-xl bg-gray-50 p-4 text-sm">
                      <p className="text-gray-600">
                        Current status:{" "}
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusConf.color}`}
                        >
                          {statusConf.label}
                        </span>
                      </p>
                    </div>
                    {renderTransactionActionButtons()}
                  </div>
                ))}
          </div>

          {viewMemberModalId ? (
            <MemberViewModal
              memberId={viewMemberModalId}
              onClose={() => setViewMemberModalId("")}
            />
          ) : null}
        </div>
      </div>
    );
  }

  // ── Create mode ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Add Reimbursement
              </h1>
              <p className="text-sm text-gray-500">
                Create a reimbursement transaction and RI form record.
              </p>
            </div>

            <button
              onClick={() => navigate("/transaction-reimbursement-list")}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to List
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Member and Eligibility</h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Select Member
                </label>
                <Select
                  value={selectedMember}
                  options={memberOptions}
                  onChange={handleMemberSelect}
                  onInputChange={handleSelectorInputChange("member", setMemberOptions)}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isLoading={selectorLoading.member || checkingMember}
                  isClearable
                  isDisabled={mode === "view"}
                  placeholder="Search member"
                  styles={selectStyles}
                  components={{ LoadingIndicator }}
                  noOptionsMessage={() => "No members found"}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Select Provider (Optional)
                </label>
                <Select
                  value={selectedProvider}
                  options={providerOptions}
                  onChange={handleProviderSelect}
                  onInputChange={handleSelectorInputChange("provider", setProviderOptions)}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isLoading={selectorLoading.provider}
                  isClearable
                  isDisabled={mode === "view"}
                  placeholder="Search provider (optional)"
                  styles={selectStyles}
                  components={{ LoadingIndicator }}
                  noOptionsMessage={() => "No providers found"}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Coverage
                </label>
                <select
                  value={form.coverage_code}
                  onChange={(e) => onChange("coverage_code", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Select coverage</option>
                  {coverages.map((coverage) => (
                    <option key={coverage.coverage_code} value={coverage.coverage_code}>
                      {coverage.coverage_code} - {coverage.coverage_desc}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleRuleCheck}
                disabled={checkingRule || mode === "view"}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-60"
              >
                {checkingRule ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faShieldAlt} />
                )}
                Rule Check
              </button>

              {memberCheck && (
                <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Member found: {memberCheck.first_name} {memberCheck.last_name}
                </span>
              )}

              {ruleResult && (
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    Number(ruleResult.result) === 0
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  Rule Check Result: {ruleResult.result}
                </span>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Transaction Details</h2>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-10">
              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Member Name</label>
                    <div className="flex items-center gap-2">
                      <input
                        value={form.member_name}
                        onChange={(e) => onChange("member_name", e.target.value)}
                        disabled
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setViewMemberModalId(form.member_id || "")}
                        disabled={!form.member_id}
                        className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View Member Info
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Plan Code{memberCheck?.plan_name ? ` (${memberCheck.plan_name})` : ""}
                    </label>
                    <input
                      value={form.plan_code}
                      onChange={(e) => onChange("plan_code", e.target.value)}
                      disabled
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Corp Code{memberCheck?.corp_name ? ` (${memberCheck.corp_name})` : ""}
                    </label>
                    <input
                      value={form.corp_code}
                      onChange={(e) => onChange("corp_code", e.target.value)}
                      disabled
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date Filed</label>
                    <input
                      type="date"
                      value={form.date_filed}
                      onChange={(e) => onChange("date_filed", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date of Treatment</label>
                    <input
                      type="date"
                      value={form.date_of_medical_treatment}
                      onChange={(e) => onChange("date_of_medical_treatment", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">(ICD Codes) Diagnosis</label>
                    <Select
                      value={selectedIcdOption}
                      options={icdOptions}
                      onChange={(selected) => onChange("icd_code", selected?.tbl_id ? String(selected.tbl_id) : "")}
                      getOptionLabel={getIcdOptionLabel}
                      getOptionValue={(option) => String(option?.tbl_id || option?.icd_10_cm || "")}
                      onInputChange={handleIcdInputChange}
                      isLoading={icdLoading}
                      isClearable
                      isDisabled={mode === "view"}
                      placeholder="Search ICD diagnosis"
                      styles={selectStyles}
                      maxMenuHeight={260}
                      components={{ LoadingIndicator }}
                      noOptionsMessage={() => "No ICD diagnosis found"}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Patient Name</label>
                    <input
                      value={form.patient_name}
                      onChange={(e) => onChange("patient_name", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Patient Member ID</label>
                    <input
                      value={form.patient_member_id}
                      onChange={(e) => onChange("patient_member_id", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Principal Name</label>
                    <div className="flex items-center gap-2">
                      <input
                        value={form.principal_name}
                        onChange={(e) => onChange("principal_name", e.target.value)}
                        disabled
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setViewMemberModalId(form.principal_member_id || "")}
                        disabled={!form.principal_member_id}
                        className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View Principal Info
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Principal ID</label>
                    <input
                      value={form.principal_member_id}
                      onChange={(e) => onChange("principal_member_id", e.target.value)}
                      disabled
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Total Claim Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.total_amt_of_claim}
                      onChange={(e) => onChange("total_amt_of_claim", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Hospital Name</label>
                    <input
                      value={form.hospital_name}
                      onChange={(e) => onChange("hospital_name", e.target.value)}
                      disabled
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Mobile No</label>
                    <input
                      value={form.mobile_no}
                      onChange={(e) => onChange("mobile_no", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Email</label>
                    <input
                      value={form.email_address}
                      onChange={(e) => onChange("email_address", e.target.value)}
                      disabled={mode === "view"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="h-full rounded-xl border border-orange-200 bg-orange-50/70 p-4">
                  <span className="inline-flex items-center rounded-full bg-orange-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    Type of Claim
                  </span>

                  <div className="mt-3 space-y-2">
                    <input
                      value={typeOfClaimSearch}
                      onChange={(e) => setTypeOfClaimSearch(e.target.value)}
                      disabled={mode === "view"}
                      placeholder="Optional search"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                    />

                    <div className="max-h-[420px] space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3">
                      {filteredTypeOfClaimCoverages.length ? (
                        filteredTypeOfClaimCoverages.map((coverage) => {
                          const coverageCode = coverage?.coverage_code || "";
                          const isChecked = selectedTypeOfClaims.includes(coverageCode);

                          return (
                            <label
                              key={coverageCode}
                              className="flex cursor-pointer items-start gap-2 text-sm text-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleTypeOfClaimSelection(coverageCode)}
                                disabled={mode === "view"}
                                className="mt-0.5"
                              />
                              <span>
                                <span className="font-semibold">{coverageCode}</span>
                                {coverage?.coverage_desc ? ` - ${coverage.coverage_desc}` : ""}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <p className="text-xs text-gray-500">No matching coverage found.</p>
                      )}
                    </div>

                    <p className="text-xs text-gray-600">
                      Selected: {selectedTypeOfClaims.length ? selectedTypeOfClaims.join(", ") : "None"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">RI Method</label>
                <select
                  value={form.ri_method}
                  onChange={(e) => onChange("ri_method", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="">Select RI Method</option>
                  <option value="FT">FT</option>
                  <option value="Check Delivery">Check Delivery</option>
                  <option value="Check Pick-up">Check Pick-up</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Pickup By</label>
                <input
                  value={form.pickup_by}
                  onChange={(e) => onChange("pickup_by", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Bank Acct. Details from App/Other details:</label>
                <textarea
                  value={form.bank_account_details}
                  onChange={(e) =>
                    onChange(
                      "bank_account_details",
                      (e.target.value || "").slice(0, MAX_BANK_ACCOUNT_DETAILS_LENGTH)
                    )
                  }
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  rows={3}
                  maxLength={MAX_BANK_ACCOUNT_DETAILS_LENGTH}
                />
                <p className="mt-1 text-right text-xs text-gray-500">
                  {(form.bank_account_details || "").length}/{MAX_BANK_ACCOUNT_DETAILS_LENGTH}
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Bank Name</label>
                <Select
                  value={selectedBankOption}
                  options={banks}
                  onChange={(selected) => onChange("bank_name", selected?.bank_name || "")}
                  getOptionLabel={getOptionLabel}
                  getOptionValue={getOptionValue}
                  isClearable
                  isDisabled={mode === "view"}
                  placeholder="Search bank"
                  styles={selectStyles}
                  components={{ LoadingIndicator }}
                  noOptionsMessage={() => "No banks found"}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Bank Branch</label>
                <input
                  value={form.bank_branch}
                  onChange={(e) => onChange("bank_branch", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Account Number</label>
                <input
                  value={form.acct_no}
                  onChange={(e) => onChange("acct_no", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Account Name</label>
                <input
                  value={form.bank_acct_name}
                  onChange={(e) => onChange("bank_acct_name", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Remarks</label>
                <textarea
                  value={form.remarks}
                  onChange={(e) => onChange("remarks", e.target.value)}
                  disabled={mode === "view"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {existingRiForm && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Existing RI form is loaded for transaction {existingRiForm.trans_no}. This page is currently in view mode for existing records.
            </div>
          )}

          {mode === "create" && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!canSubmit || loading}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSave} />
                )}
                Create Reimbursement
              </button>
            </div>
          )}
        </form>
      </div>

      {viewMemberModalId ? (
        <MemberViewModal
          memberId={viewMemberModalId}
          onClose={() => setViewMemberModalId("")}
        />
      ) : null}
    </div>
  );
};

export default ReimbursementAdd;
